'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FaEllipsisH } from 'react-icons/fa'
import ClipLoader from 'react-spinners/ClipLoader'

// Button styles shared by the media-card overlay actions (generate feed, gallery
// cards). Primary (Clone/Animate/Extend) uses the brand tint; secondary icons
// stay frosted dark. The pill is icon-only until the card is ≥380px wide — the
// `@[380px]:` variants need an `@container` ancestor to take effect.
export const OVERLAY_PILL =
  'h-11 w-11 justify-center px-0 @[380px]:w-auto @[380px]:justify-start @[380px]:px-4 rounded-2xl bg-[var(--main-bg-color)] hover:opacity-90 text-white text-sm font-semibold inline-flex items-center gap-2 transition-opacity disabled:opacity-40 disabled:pointer-events-none'
export const OVERLAY_ICON =
  'h-11 w-11 rounded-2xl bg-black/60 hover:bg-black/75 border border-white/20 inline-flex items-center justify-center text-white transition-colors disabled:opacity-40 disabled:pointer-events-none'

const PANEL_CLASSES =
  'min-w-[160px] py-1 rounded-xl bg-[#0b0a0f]/95 border border-white/15 shadow-xl'

/**
 * Frosted "More options" dropdown used on the image/video result overlays.
 *
 * Props:
 *  - items: array of { key, icon, label, onClick, destructive?, disabled?, muted?, loading? }
 *    (`muted`: looks unavailable but can still be tapped, to say why: a picture that can't be kept)
 *  - onOpenChange?(open): notified when the menu opens/closes so the parent can
 *    keep the hover overlay visible while the menu is open.
 *  - portal?: render the panel into document.body with fixed positioning.
 *    Needed when an ancestor has overflow-hidden (e.g. gallery card wrappers)
 *    that would otherwise clip the panel. The menu closes on scroll/resize
 *    instead of tracking the trigger.
 *  - className?: extra classes for the trigger button.
 */
export default function OverlayActionsMenu({
  items = [],
  onOpenChange,
  portal = false,
  className = '',
  // A signup card owns the link; reuse this exact trigger without nesting a
  // button or opening member actions inside that link.
  presentationOnly = false,
}) {
  const [open, setOpen] = useState(false)
  // Key of an in-flight action whose item shows a spinner; the menu stays open
  // until it resolves rather than closing on click.
  const [pendingKey, setPendingKey] = useState(null)
  const [panelStyle, setPanelStyle] = useState(null)
  const wrapperRef = useRef(null)
  const panelRef = useRef(null)

  const setOpenState = (next) => {
    setOpen(next)
    onOpenChange?.(next)
    if (!next) setPendingKey(null)
  }

  // Anchor the fixed panel to the trigger, flipping above it when the viewport
  // bottom is too close (height estimated from the item count).
  const toggleOpen = () => {
    const next = !open
    if (next && portal) {
      const rect = wrapperRef.current?.getBoundingClientRect()
      if (rect) {
        const estimatedHeight = items.length * 42 + 10
        const openUp =
          rect.bottom + 8 + estimatedHeight > window.innerHeight &&
          rect.top - 8 - estimatedHeight > 0
        setPanelStyle({
          right: Math.max(8, window.innerWidth - rect.right),
          ...(openUp
            ? { bottom: window.innerHeight - rect.top + 8 }
            : { top: rect.bottom + 8 }),
        })
      }
    }
    setOpenState(next)
  }

  // Close once the pending action stops loading — finished (success/error), or it
  // never started (a gated/early-return action), in which case it closes at once.
  const pendingLoading = !!items.find((i) => i.key === pendingKey)?.loading
  useEffect(() => {
    if (pendingKey !== null && !pendingLoading) setOpenState(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey, pendingLoading])

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        !panelRef.current?.contains(e.target)
      ) {
        setOpenState(false)
      }
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpenState(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // The portal panel is fixed-positioned, so it can't follow the trigger while
  // the page scrolls or resizes — just dismiss it.
  useEffect(() => {
    if (!open || !portal) return

    const handleDismiss = () => setOpenState(false)
    window.addEventListener('scroll', handleDismiss, true)
    window.addEventListener('resize', handleDismiss)
    return () => {
      window.removeEventListener('scroll', handleDismiss, true)
      window.removeEventListener('resize', handleDismiss)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, portal])

  const panel = open ? (
    <div
      ref={panelRef}
      role="menu"
      style={portal ? panelStyle : undefined}
      className={
        portal
          ? `fixed z-[100] ${PANEL_CLASSES}`
          : `absolute right-0 top-full mt-2 z-30 ${PANEL_CLASSES}`
      }
    >
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          disabled={item.disabled || item.loading}
          onClick={() => {
            item.onClick?.()
            // Items with a loading indicator keep the menu open until the
            // action resolves; the rest close immediately.
            if (item.loading !== undefined) {
              setPendingKey(item.key)
            } else {
              setOpenState(false)
            }
          }}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-left transition-colors hover:bg-white/10 disabled:opacity-50 disabled:pointer-events-none ${
            item.destructive ? 'text-red-400' : 'text-white'
          }${item.muted ? ' opacity-50' : ''}`}
        >
          <span className="inline-flex w-4 items-center justify-center">
            {item.loading ? (
              <ClipLoader color="#ffffff" size={14} />
            ) : (
              item.icon
            )}
          </span>
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  ) : null

  const Trigger = presentationOnly ? 'span' : 'button'

  return (
    <div className="relative" ref={wrapperRef}>
      <Trigger
        type={presentationOnly ? undefined : 'button'}
        title="More options"
        aria-label="More options"
        onClick={presentationOnly ? undefined : toggleOpen}
        className={`${OVERLAY_ICON} ${className}`}
      >
        <FaEllipsisH className="text-base" />
      </Trigger>

      {portal && panel ? createPortal(panel, document.body) : panel}
    </div>
  )
}
